import { useMemo } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import MoreHorizIcon from '@/material-icons/400-24px/more_horiz.svg?react';
import { changeAdvancedLayout } from 'mastodon/actions/advanced_layout';
import { ColumnLink } from 'mastodon/features/ui/components/column_link';
import { useIdentity } from 'mastodon/identity_context';
import { forceSingleColumn } from 'mastodon/initial_state';
import type { MenuItem } from 'mastodon/models/dropdown_menu';
import { canManageReports, canViewAdminDashboard } from 'mastodon/permissions';
import { useAppDispatch } from 'mastodon/store';

import { CollapsiblePanel } from './collapsible_panel';

const messages = defineMessages({
  more: { id: 'navigation_bar.more', defaultMessage: 'More' },
  expand: {
    id: 'navigation_panel.expand_more',
    defaultMessage: 'Expand more menu',
  },
  collapse: {
    id: 'navigation_panel.collapse_more',
    defaultMessage: 'Collapse more menu',
  },
  blocks: { id: 'navigation_bar.blocks', defaultMessage: 'Blocked users' },
  mutes: { id: 'navigation_bar.mutes', defaultMessage: 'Muted users' },
  administration: {
    id: 'navigation_bar.administration',
    defaultMessage: 'Administration',
  },
  moderation: { id: 'navigation_bar.moderation', defaultMessage: 'Moderation' },
  toggleColumnMode: {
    id: 'navigation_bar.toggle_column_mode',
    defaultMessage: 'Toggle Column mode',
  },
  toggleFlatView: {
    id: 'navigation_bar.toggle_flat_view',
    defaultMessage: 'Toggle flat view',
  },
});

export const MoreLink: React.FC = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const { permissions } = useIdentity();

  const menu = useMemo(() => {
    const arr: MenuItem[] = [
      {
        to: '/mutes',
        text: intl.formatMessage(messages.mutes),
      },
      {
        to: '/blocks',
        text: intl.formatMessage(messages.blocks),
      },
    ];

    if (canManageReports(permissions)) {
      arr.push(null, {
        href: '/admin/reports',
        text: intl.formatMessage(messages.moderation),
      });
    }

    if (canViewAdminDashboard(permissions)) {
      arr.push({
        href: '/admin/dashboard',
        text: intl.formatMessage(messages.administration),
      });
    }

    arr.push(null, {
      text: intl.formatMessage(
        forceSingleColumn ? messages.toggleColumnMode : messages.toggleFlatView,
      ),
      action: () => {
        dispatch(
          changeAdvancedLayout(
            forceSingleColumn,
            forceSingleColumn ? '/deck/' : undefined,
          ),
        );
      },
    });

    return arr;
  }, [intl, dispatch, permissions]);

  return (
    <CollapsiblePanel
      icon='ellipsis-h'
      iconComponent={MoreHorizIcon}
      title={intl.formatMessage(messages.more)}
      collapseTitle={intl.formatMessage(messages.collapse)}
      expandTitle={intl.formatMessage(messages.expand)}
      defaultExpanded
    >
      {menu.map((item, index) => {
        if (item === null) {
          return <hr key={`sep-${index}`} />;
        }

        if ('action' in item) {
          return (
            <button
              key={item.text}
              type='button'
              className='column-link column-link--transparent'
              onClick={item.action}
            >
              <span>{item.text}</span>
            </button>
          );
        }

        if ('href' in item) {
          return (
            <ColumnLink
              key={item.text}
              transparent
              href={item.href}
              method={item.method}
              icon=''
              text={item.text}
            />
          );
        }

        return (
          <ColumnLink
            key={item.text}
            transparent
            to={item.to}
            icon=''
            text={item.text}
          />
        );
      })}
    </CollapsiblePanel>
  );
};
